import { Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import FormError from 'dashboard/components/FormControls/FormError';
import ReactSelect from 'dashboard/components/ReactSelect';
import RichTextEditor from 'dashboard/components-lib/RichTextEditor';
import { NextPageSetting, NextPageSettingOptions } from 'dashboard/lib/constants/case';
import { CaseCommentTemplate, CaseState, StateTag } from 'dashboard/models/case-template';
import { Tag } from 'dashboard/models/tag';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { useFormSecurely } from 'lib/security/reactHookForm';

const Section = styled('div')`
  margin-bottom: ${Spacing[5]};
`;

const Label = styled('h4')`
  margin-bottom: ${Spacing[2]};
`;

export interface FormSetCaseStateProps {
  handleCancel: () => void;
  state: CaseState;
  stateTag: StateTag;
  tags: Tag[];
  setCaseStateAndClose: (values: FormSetCaseStateData) => void;
  defaultValues: Partial<FormSetCaseStateData>;
  caseCommentTemplates: CaseCommentTemplate[];
}

const FormSetCaseState = ({
  handleCancel,
  setCaseStateAndClose,
  state,
  stateTag,
  tags,
  defaultValues,
  caseCommentTemplates,
}: FormSetCaseStateProps) => {
  const {
    setValue,
    handleSubmit,
    register,
    control,
    formState: { isSubmitting, isValid, errors },
  } = useFormSecurely<FormSetCaseStateData>({
    defaultValues,
    mode: 'onChange',
  });

  const [editorKey, setEditorKey] = useState(uuid());

  const caseCommentItems = caseCommentTemplates.map((template) => {
    return {
      id: template.token,
      label: template.name,
      onClick: () => {
        setValue('message', template.body, { shouldDirty: true, shouldValidate: true });
        setEditorKey(uuid());
      },
    };
  });

  // TODO: (melissa) figure out how to let a user allow creating tags
  return (
    <form onSubmit={handleSubmit(setCaseStateAndClose)}>
      <Section>
        <Label>Status</Label>
        <CaseStatusSticker caseState={state} />
        <Spacer height={3} />
        {stateTag && stateTag['available-tags'].length > 0 && (
          <>
            <Label>
              {stateTag['is-tag-required']
                ? 'Reason for status change'
                : 'Reason for status change (optional)'}
            </Label>
            <Controller
              control={control}
              {...register('tags', {
                required: stateTag['is-tag-required'] ? 'Please select a tag.' : false,
              })}
              render={({ field: { onChange, value } }) => (
                <ReactSelect
                  id='form-set-case-state__tags'
                  data-test='form-set-case-state__tags'
                  isMulti={stateTag['is-multi-tags-allowed']}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  options={stateTag['available-tags']
                    .map((at) => {
                      const tag = tags.find((t) => t.id === at);
                      if (!tag) {
                        return null;
                      }
                      return {
                        label: tag.attributes.name,
                        value: tag.id,
                      };
                    })
                    .filter(Boolean)}
                  portal={true}
                  isSearchable={true}
                  placeholder={stateTag['is-multi-tags-allowed'] ? 'Tags...' : 'Tag...'}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </>
        )}
        {errors['tags'] && <FormError>{errors['tags'].message}</FormError>}
        <Spacer height={2} />
        <Label>Comment ({state['comment-required'] ? 'required' : 'optional'})</Label>
        {caseCommentTemplates.length > 0 && (
          <>
            <DropdownMenu
              trigger={
                <Button
                  id='form-add-case-comment__preset'
                  tabIndex={0}
                  variant='basic'
                  width='auto'
                  suffixIcon={<CaretDownIcon />}
                  aria-label='Preset comment'
                >
                  Presets
                </Button>
              }
              align='start'
            >
              {caseCommentItems.map((item) => (
                <DropdownMenu.Item id={item.id} onClick={item.onClick} key={item.id}>
                  {item.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu>
            <Spacer height={2} />
          </>
        )}
        <Controller
          name='message'
          control={control}
          rules={{
            required: state['comment-required'] ? 'Please enter a comment.' : false,
          }}
          render={({ field: { value, onChange } }) => (
            <RichTextEditor
              defaultValue={value}
              onChange={onChange}
              key={editorKey}
              aria-label='message'
            />
          )}
        />
        {errors['message'] && <FormError>{errors['message'].message}</FormError>}
        <Spacer height={1} />
        <RadioGroup>
          {NextPageSettingOptions.map(({ label, value }) => {
            return (
              <Radio key={value} label={label} {...register('nextPageSetting')} value={value} />
            );
          })}
        </RadioGroup>
      </Section>
      <HStack equal={true}>
        <Button
          variant='basic'
          id='form-set-case-state__cancel'
          data-test='form-set-case-state__cancel'
          disabled={isSubmitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='form-set-case-state__submit'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
        >
          Update
        </Button>
      </HStack>
    </form>
  );
};

export interface FormSetCaseStateData {
  message: string;
  tags: string[];
  nextPageSetting?: NextPageSetting;
}

export default FormSetCaseState;
